import React, { useEffect, useState } from "react";
import { Layout, Menu, Button, Popover, Spin, Select } from "antd";
import {
  UploadOutlined,
  MessageOutlined,
  DashboardOutlined,
  CommentOutlined,
  FolderOutlined,
  FileOutlined,
  SettingOutlined,
  UserOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  TeamOutlined,
  MenuFoldOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import EvaLogo from "../../images/EVA.png";
import useChat from "../../stores/useChat";
import useAppSession from "../../stores/useAppSession";
import { useNavigate } from "react-router-dom";
import fileApi from "../../services/api/fileApi";
import FileUploadModal from "../FileUploadModal/FileUploadModal.jsx";
import FileListModal from "../FileListModal/FileListModal.jsx";
import YouTubeModal from "../YoutubeModal/YoutubeModal.jsx";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";

const { Sider } = Layout;
const { Option } = Select;

const SidebarAntd = ({ children }) => {
  const intl = useIntl();
  const [collapsed, setCollapsed] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);
  const [isYouTubeModalOpen, setIsYouTubeModalOpen] = useState(false);
  const [youtubeUrl, setYouTubeUrl] = useState([]);

  const {
    session,
    habilitaImagens,
    habilitaEmail,
    habilitaZendesk,
    habilitaContexto,
    isTyping,
    botTyping,
    engine,
    setSession,
    setHabilitaImagens,
    setHabilitaEmail,
    setHabilitaZendesk,
    setHabilitaContexto,
    setEngine,
    setShouldLog,
    temperature,
    setTemperature,
  } = useChat();

  const {
    clientId,
    sessionId,
    setSessionId,
    clearSession,
    roles = [],
    locale,
    setLocale,
  } = useAppSession();
  const navigate = useNavigate();

  const toggleCollapsed = () => setCollapsed(!collapsed);

  const handleNewChat = () => {
    setShouldLog(true);
    navigate("/");
    setSession("");
    setSessionId("");
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await fileApi.getFiles(clientId);
      if (response.status !== 200) throw new Error("Erro ao carregar arquivos");
      const data = await response.data;
      const arrFiles = Object.entries(data).map(([file, date]) => ({ file, date }));
      setFiles(arrFiles);
    } catch (error) {
      console.error("Erro ao carregar arquivos:", error);
      toast.error(intl.formatMessage({ id: "sidebar.errorLoadingFiles", defaultMessage: "Erro ao carregar arquivos" }));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearSession();
    navigate("/login");
  };

  const handleOpenProfile = () => navigate("/user-profile");
  const handleOpenDashboard = () => navigate("/dashboard");
  const handleOpenMessageboard = () => navigate("/messageboard");
  const handleOpenWorkspace = () => navigate("/workspace");
  const handleOpenYouTube = () => setIsYouTubeModalOpen(true);
  const handleLanguageChange = (value) => setLocale(value);
  const handleTemperatureChange = (value) => setTemperature(value);

  const profileContent = (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {(roles.includes("ADMIN") || roles.includes("ADMIN_IA")) && (
        <Button type="text" icon={<UserOutlined />} onClick={handleOpenProfile}>
          {intl.formatMessage({ id: "sidebar.profile", defaultMessage: "Perfil" })}
        </Button>
      )}
      <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}>
        {intl.formatMessage({ id: "sidebar.logout", defaultMessage: "Logout" })}
      </Button>
    </div>
  );

  const menuItems = [
    {
      key: "1",
      icon: <MessageOutlined />,
      label: intl.formatMessage({ id: "sidebar.newChat", defaultMessage: "Novo Chat" }),
      onClick: handleNewChat,
      disabled: isTyping || botTyping,
    },
  ];

  if (roles.some(r => ["ADMIN", "ADMIN_IA"].includes(r))) {
    menuItems.push(
      {
        key: "2",
        icon: <DashboardOutlined />,
        label: intl.formatMessage({ id: "sidebar.dashboard", defaultMessage: "Dashboard" }),
        onClick: handleOpenDashboard,
      },
      {
        key: "3",
        icon: <CommentOutlined />,
        label: intl.formatMessage({ id: "sidebar.messages", defaultMessage: "Mensagens" }),
        onClick: handleOpenMessageboard,
      },
      {
        key: "4",
        icon: <FolderOutlined />,
        label: intl.formatMessage({ id: "sidebar.files", defaultMessage: "Arquivos" }),
        children: [
          {
            key: "4-1",
            icon: <UploadOutlined />,
            label: intl.formatMessage({ id: "sidebar.uploadFiles", defaultMessage: "Carregar arquivos" }),
            onClick: () => setIsModalUploadOpen(true),
          },
          {
            key: "4-2",
            icon: <FileOutlined />,
            label: intl.formatMessage({ id: "sidebar.showFiles", defaultMessage: "Mostrar arquivos" }),
            onClick: () => { setIsModalOpen(true); fetchFiles(); },
          },
        ],
      },
      {
        key: "5",
        icon: <SettingOutlined />,
        label: intl.formatMessage({ id: "sidebar.settings", defaultMessage: "Configurações" }),
      },
      {
        key: "6",
        icon: <TeamOutlined />,
        label: intl.formatMessage({ id: "sidebar.workspace", defaultMessage: "Workspace" }),
        onClick: handleOpenWorkspace,
      },
      {
        key: "8",
        icon: <YoutubeOutlined />,
        label: intl.formatMessage({ id: "sidebar.youtube", defaultMessage: "YouTube" }),
        onClick: handleOpenYouTube,
      }
    );
  }

  if (roles.includes("ADMIN") || roles.includes("ACC")) {
    menuItems.push({
      key: "9",
      icon: <TeamOutlined />,
      label: intl.formatMessage({ id: "sidebar.controleDeContas", defaultMessage: "Controle de Contas" }),
      onClick: () => navigate("/expense-management"),
    });
  }

  return (
    <div style={{ display: "flex" }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={280}
        collapsedWidth={80}
        style={{ height: "100vh", position: "fixed", left: 0 }}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

          <div style={{ display: "flex", justifyContent: "space-between", padding: 10 }}>
            {!collapsed && <img src={EvaLogo} alt="Logo" style={{ width: 40 }} />}
            <Button type="primary" onClick={() => setCollapsed(!collapsed)}>
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </Button>
          </div>


          <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
            <Popover content={profileContent} trigger="click" placement="rightTop">
              <Button
                type="text"
                style={{
                  fontSize: 16,
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  width: "auto",
                  margin: 10,
                }}
              >
                <UserOutlined style={{ fontSize: 20 }} />
                {!collapsed && intl.formatMessage({ id: "sidebar.profile", defaultMessage: "Perfil" })}
              </Button>
            </Popover>
          </div>


          <Menu theme="dark" mode="inline" items={menuItems} style={{ flex: 1, overflowY: "auto" }} />
        </div>


        <FileUploadModal isOpen={isModalUploadOpen} onClose={() => setIsModalUploadOpen(false)} />
        <FileListModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} files={files} />
        <YouTubeModal isOpen={isYouTubeModalOpen} onClose={() => setIsYouTubeModalOpen(false)} youtubeUrls={youtubeUrl} setYouTubeUrls={setYouTubeUrl} />
      </Sider>

      <div style={{ marginLeft: collapsed ? 80 : 280, width: "100%" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Spin size="large" />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

export default SidebarAntd;
